import { useMemo } from 'react';
import styles from './PlanCard.module.scss';
import {
  useEntitlements,
  usePaymentControlState,
  useSubscriptionInfo,
} from 'src/hooks';
import { PaymentTabs, PlanKind, SubscriptionPlanInfo } from 'src/types';
import { PlanCardTitle } from 'src/pages/PaymentBetaPage/componentsV3/PlanCardTitle';
import { PlanCardPrice } from 'src/pages/PaymentBetaPage/componentsV3/PlanCardPrice';
import { SelectedPlanButton } from 'src/pages/PaymentBetaPage/components/SelectedPlanButton';
import { UserTier } from 'src/types/wallet/models/UserTier';
import { GetPlanButton } from 'src/pages/PaymentBetaPage/componentsV3/GetPlanButton';
import { PlanCardContent } from 'src/pages/PaymentBetaPage/componentsV3/PlanCardContent';
import { NinjaSubscriptionPlan } from 'src/types/wallet/models/NinjaSubscriptionPlan';
import { RequestsPerModeSection } from 'src/pages/PaymentBetaPage/componentsV3/RequestsPerModeSection';
import { PlanCardDescription } from 'src/pages/PaymentBetaPage/componentsV3/PlanCardDescription';

interface PlanCardProps {
  sectionPlan: SubscriptionPlanInfo;
  onOpenCheckout: () => void;
}

export const PlanCard = ({ sectionPlan, onOpenCheckout }: PlanCardProps) => {
  const { plan_kind, plan_family, plan_tier } = sectionPlan;
  const { selectedPlan, selectedPaymentTab, selectedPeriod } =
    usePaymentControlState();
  const { subscriptionInfo } = useSubscriptionInfo();

  const isBusiness = selectedPaymentTab === PaymentTabs.BUSINESS;

  const { entitlements, isLoading } = useEntitlements({
    isBusiness: isBusiness,
    interval: selectedPeriod,
  });

  const entitlement = useMemo(() => {
    return entitlements.find(
      (item) => item.tier.toString() === plan_tier.toString(),
    )?.limits;
  }, [entitlements, plan_tier]);

  const maxEntitlement = useMemo(() => {
    return entitlements.find(
      (item) =>
        item.tier.toString() ===
        (isBusiness
          ? NinjaSubscriptionPlan.BUSINESS_SUPERNINJA_MAX
          : NinjaSubscriptionPlan.SUPERNINJA_MAX
        ).toString(),
    )?.limits;
  }, [entitlements, isBusiness]);

  const subscribedTier = useMemo(() => {
    return subscriptionInfo?.plan_tier;
  }, [subscriptionInfo]);

  const isCurrentPlan = useMemo(() => {
    return subscriptionInfo?.plan_kind === plan_kind;
  }, [subscriptionInfo, plan_kind]);

  const isSelected = selectedPlan === plan_kind;

  return (
    <div className={styles.root}>
      <div className={styles.mainContainer}>
        <div className={styles.headerWrapper}>
          {/* There is a bug with type generation. SubscriptionPlanInfo.plan_kind has to be the PlanKind type, not string */}
          <PlanCardTitle planKind={plan_kind as PlanKind} />

          <PlanCardPrice sectionPlan={sectionPlan} />
        </div>
        <div className={styles.buttonWrapper}>
          {isSelected ? (
            <SelectedPlanButton />
          ) : (
            <GetPlanButton
              isCurrentPlan={isCurrentPlan}
              subscribedTier={subscribedTier}
              planFamily={plan_family}
              planKind={plan_kind as PlanKind}
              onOpenCheckout={onOpenCheckout}
            />
          )}
        </div>

        <PlanCardDescription
          totalRequests={entitlement?.total?.value || 0}
          isEntitlementLoading={isLoading}
        />
        <RequestsPerModeSection
          standard={entitlement?.standard_requests?.value || 0}
          complex={entitlement?.complex_requests?.value || 0}
          fast={entitlement?.fast_requests?.value || 0}
          total={(maxEntitlement?.complex_requests?.value || 0) * 1.1} // it uses logic that complex is 90%
        />

        <PlanCardContent
          planFamily={plan_family}
          planKind={plan_kind as PlanKind}
          planTier={plan_tier as unknown as UserTier}
        />
      </div>

      <p className={styles.footerCaption}>
        Cancel anytime, effective next billing cycle
      </p>
    </div>
  );
};
